import { inject, Injectable, OnDestroy } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { LocalStorageService } from '@df/browser/storage/local-storage.service';
import { Catalog } from '@df/catalog/catalog.interfaces';
import { App } from '@df/core/app';
import { Meta } from '@df/core/meta/meta';
import { Client } from '@df/core/service/client';
import { injectTimeoutService } from '@df/ng/timeout.service';
import { CustomerModel } from '@df/session/api/customer.model';
import { AfterContentInitDirectiveService } from '@df/ui/common/after-content-init.directive';
import { AbstractDfModalInitiatorService } from '@df/ui/modal/abstract-df-modal-initiator.service';
import { NDfModal } from '@df/ui/modal/df-modal.interface';
import { ICmsModalService } from '@icms/client/assets/modal/icms-modal.service';
import { ICmsBlock } from '@icms/core/resource/block/icms-block';
import { Subscription } from 'rxjs/internal/Subscription';
import { filter } from 'rxjs/internal/operators/filter';
import { skip } from 'rxjs/internal/operators/skip';
import { isCheckoutUrl } from '../../../ui/common/if-checkout-process.service';
import { SIGN_UP_POPUP_NAME } from './sign-up-popup-content.component';
import { EInitLogic, ISignUpPopupComponentData } from './sign-up-popup.interfaces';

@Injectable({
    providedIn: 'root'
})
export class SignUpPopupInitiatorService extends AbstractDfModalInitiatorService implements OnDestroy {
    protected subscriptions = new Subscription();
    protected subscription?: Subscription;

    protected meta = inject(Meta);
    protected client = inject(Client);
    protected afterContentInitDirectiveService = inject(AfterContentInitDirectiveService);
    protected customerModel = inject(CustomerModel);
    protected router = inject(Router);
    protected localStorageService = inject(LocalStorageService);
    protected timeoutService = injectTimeoutService();

    constructor(dfModalService: ICmsModalService, app: App) {
        super(dfModalService, app);
    }

    ngOnDestroy(): void {
        this.subscription?.unsubscribe();
        this.subscriptions.unsubscribe();
    }

    override initiate(modal: NDfModal.IConfig): Promise<void> {
        this.subscriptions.add(
            this.router.events
                .pipe(
                    skip(1),
                    filter(e => e instanceof NavigationStart)
                )
                .subscribe(() => {
                    this.subscription?.unsubscribe();
                })
        );

        return super.initiate(modal);
    }

    listen(cb: () => void, modal: NDfModal.IConfig): Promise<void> {
        return new Promise(resolve => {
            this.subscription = this.afterContentInitDirectiveService.loaded.subscribe(value => {
                if (value) {
                    this.dfModalService
                        .loadDependencies(modal)
                        .then((block: void | ICmsBlock<Catalog.IBlockData<ISignUpPopupComponentData>>) => {
                            if (!block || (!!block.data?.content.styles.hidden && !this.client.isCms)) {
                                return;
                            }

                            const cbWithTest = () => {
                                if (!this.customerModel.isLoggedIn) {
                                    const customerGroups = block.data?.content.init_customer_groups?.trim();
                                    const groupsAllowed = customerGroups ? customerGroups.split(',').map(i => Number(i)) : undefined;

                                    if (
                                        (!groupsAllowed ||
                                            (this.customerModel.data.group_id &&
                                                groupsAllowed.includes(this.customerModel.data.group_id))) &&
                                        this.canShow()
                                    ) {
                                        cb();
                                    }
                                }
                            };

                            this.customerModel
                                .load()
                                .then(() => {
                                    if (!this.customerModel.isLoggedIn) {
                                        const initClicks = block.data?.content.init_clicks || 0;
                                        let clicks = 0;
                                        let subscription: Subscription;

                                        switch (block.data?.content?.init_logic) {
                                            case EInitLogic.clicks:
                                                subscription = this.app.meta.subscribe(() => {
                                                    if (++clicks >= initClicks) {
                                                        cbWithTest();
                                                        subscription.unsubscribe();
                                                    }
                                                });
                                                break;
                                            case EInitLogic.delayed:
                                                this.timeoutService.setTimeout(cbWithTest, block.data.content.init_delay * 1000);
                                                break;
                                            default:
                                                cbWithTest();
                                        }
                                    }
                                })
                                .finally(resolve);
                        });
                }
            });
        });
    }

    /**
     * Checks whether it can display
     */
    protected canShow(): boolean {
        const url = this.router.url;

        // already closed
        if (this.localStorageService.get(SIGN_UP_POPUP_NAME)) {
            return false;
        }

        if (isCheckoutUrl(url) || !!url.match(/^\/basket/) || this.meta.model?.data?.type === 'product') {
            return false;
        }

        if (this.client.isCms) {
            return false;
        }

        return true;
    }
}
